import type { DateRange } from '@/components/analytics';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { useOrganization } from '@/context/organization-context';
import { useAnalyticsControllerGetOrderAnalytics } from '@/apis/backend/generated/analytics/analytics';
import { MetricCard, PeriodSelector, TimeSeriesChart, useDefaultDateRange } from '@/components/analytics';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/analytics/orders')({
  component: OrderAnalyticsPage,
});

function formatCurrency(value: number): string {
  return `$${value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value.toLocaleString()}`;
}

function OrderAnalyticsPage() {
  const { t } = useTranslation('analytics');
  const { activeOrganization, activeClient } = useOrganization();
  const defaultRange = useDefaultDateRange(30);
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);

  const orgId = activeOrganization?.id;
  const clientId = activeClient?.id;

  const { data, isLoading } = useAnalyticsControllerGetOrderAnalytics(
    {
      orgId: orgId!,
      clientId: clientId!,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      previousStartDate: dateRange.previousStartDate,
      previousEndDate: dateRange.previousEndDate,
    },
    { query: { enabled: !!orgId && !!clientId, staleTime: 60000 } }
  );

  if (!orgId || !clientId) return null;

  return (
    <Stack spacing={3}>
      <PeriodSelector value={dateRange} onChange={setDateRange} />

      {isLoading ? (
        <Stack direction="row" spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={100} sx={{ flex: 1, borderRadius: '12px' }} />
          ))}
        </Stack>
      ) : data ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            <MetricCard
              title={t('orders.metrics.totalRevenue', 'Total Revenue')}
              metric={data.totalRevenue}
              format="currency"
            />
            <MetricCard
              title={t('orders.metrics.totalOrders', 'Total Orders')}
              metric={data.totalOrders}
            />
            <MetricCard
              title={t('orders.metrics.avgOrderValue', 'Avg Order Value')}
              metric={data.avgOrderValue}
              format="currency"
            />
          </Box>

          {/* Order Revenue Time Series */}
          {data.timeSeries && data.timeSeries.length > 0 && (
            <TimeSeriesChart
              title={t('orders.chart.title', 'Order Revenue Over Time')}
              data={data.timeSeries}
              series={[
                { dataKey: 'orderCount', name: t('orders.chart.seriesOrders', 'Orders'), type: 'bar', yAxisId: 'left' },
                { dataKey: 'revenue', name: t('orders.chart.seriesRevenue', 'Revenue'), type: 'line', color: '#1B5E20', yAxisId: 'right' },
              ]}
              formatRightAxis={(v) => formatCurrency(v)}
              formatTooltip={(value, name) =>
                name === t('orders.chart.seriesRevenue', 'Revenue')
                  ? `$${value.toLocaleString()}`
                  : value.toLocaleString()
              }
            />
          )}
        </>
      ) : (
        <Typography color="text.secondary">{t('orders.empty', 'No order data available for this period.')}</Typography>
      )}
    </Stack>
  );
}
